import React, { useState } from 'react';
import { useSelector, useDispatch, RootStateOrAny } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { AxiosError } from 'axios';
import api from '~/services';
import { log } from '~/helpers';
import { resetPasswordSchema } from '~/validations/form';
import * as lib from '~/dependencies';
import * as actions from '~/store/actions';
import * as NuacomComponents from '~/components';
import * as AuthStyles from '../Auth/styles';
import * as AuthComponents from '../Auth/components';

const MIN_PASSWORD_LENGTH = 8;

export default function ResetPassword() {
  const [formErrors, setFormErrors] = useState([]);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newestPassword, setNewestPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [allowSave, setAllowSave] = useState(false);

  const appVersion = useSelector(
    (state: RootStateOrAny) => state.system?.version
  );

  const passwordMinLength = useSelector(
    (state: RootStateOrAny) =>
      state.auth?.passExpirationReqs?.passwordMinLength?.value ||
      MIN_PASSWORD_LENGTH
  );

  const email = useSelector((state: RootStateOrAny) => state.auth?.email);
  const password = useSelector((state: RootStateOrAny) => state.auth?.password);

  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = lib.useTranslation();

  const getErrorMessage = (message: string) => {
    let msg = t('screens.auth.redefinePassword.errors.generic');

    switch (message) {
      case 'This password has been used before. Please choose a unique password for security purposes.':
        msg = `${t('screens.auth.redefinePassword.errors.noPassReuse')}`;
        break;
      case 'The given new password has appeared in a data leak. Please choose a different new password.':
        msg = `${t('screens.auth.redefinePassword.errors.noDataLeak')}`;
        break;
      default:
    }

    return msg;
  };

  async function resetUserPassword() {
    lib.ToastMessage(false);

    try {
      const response = await api.users.post.changePassword(
        email,
        password,
        newestPassword,
        confirmPassword
      );

      if (response?.status === 202) {
        lib.ToastMessage('success', t('screens.auth.redefinePassword.success'));
        setTimeout(() => history.push('/'), 300);
      }
    } catch (err) {
      const error = err as AxiosError;
      const response = error.response?.data;
      let errorMessage = t('screens.auth.redefinePassword.errors.generic');

      // @ts-ignore
      if (response?.message === 'The given data was invalid.') {
        // @ts-ignore
        const msg = response?.errors?.new_password[0];
        errorMessage = getErrorMessage(msg);
      } else {
        errorMessage = t('screens.auth.redefinePassword.errors.noPassReuse');
      }

      lib.ToastMessage('error', errorMessage, 7000);
    }
  }

  async function validateForm() {
    try {
      setLoading(true);

      await resetPasswordSchema.validate(
        {
          currentPassword,
          newestPassword,
          confirmPassword,
        },
        { abortEarly: false }
      );

      await resetUserPassword();
    } catch (err) {
      log.error('Failed to execute reset password form validator.', err);
    } finally {
      setFormErrors([]);
      setLoading(false);
    }
  }

  function onSubmit(e: any) {
    e.preventDefault();
  }

  function onGoBack() {
    dispatch(actions.resetAuth(true));
    history.push('/auth');
  }

  function onInputKeyPress(event: { key: string }) {
    if (event.key === 'Enter') {
      validateForm();
    }
  }

  return (
    <div id="contentNuacom">
      <div id="mainContent">
        {/* <AuthStyles.BackgroundFloat /> */}
        <form onSubmit={onSubmit}>
          <AuthStyles.BoxLogin>
            <AuthComponents.LogoAuth />

            <AuthStyles.Space height={20} />
            <h2>{t('screens.auth.redefinePassword.title')}</h2>
            <AuthStyles.Space height={15} />

            {/* @ts-ignore FIXME: (Legacy) Missing Password Props */}
            <AuthComponents.InputLogin
              password
              name="current-password"
              data-test-id="current-password"
              placeholder={t('screens.auth.redefinePassword.txtCurrentPass')}
              value={currentPassword}
              onChange={(data: { value: string }) =>
                setCurrentPassword(data.value.trim())
              }
              validator={formErrors}
              loading={loading}
              onKeyPress={onInputKeyPress}
            />
            {/* @ts-ignore FIXME: (Legacy) Missing Password Props */}
            <AuthComponents.InputLogin
              password
              name="newest-password"
              data-test-id="newest-password"
              placeholder={t('screens.auth.redefinePassword.txtNewPass')}
              value={newestPassword}
              onChange={(data: { value: string }) =>
                setNewestPassword(data.value.trim())
              }
              validator={formErrors}
              loading={loading}
              onKeyPress={onInputKeyPress}
            />

            <NuacomComponents.PasswordStrengthIndicator
              password={newestPassword}
            />
            <AuthStyles.Space height={10} />

            <AuthComponents.PasswordChecklist
              rules={[
                'minLength',
                'mustIncludeSymbols',
                'mustMixedCase',
                'mustIncludeNumbers',
                'match',
              ]}
              minLength={passwordMinLength}
              currentPassword={currentPassword}
              value={newestPassword}
              confirmPassword={confirmPassword}
              onChange={isValid => setAllowSave(isValid)}
            />
            <AuthStyles.Space height={10} />

            {/* @ts-ignore FIXME: (Legacy) Missing Password Props */}
            <AuthComponents.InputLogin
              password
              name="password-confirmation"
              data-test-id="password-confirmation"
              placeholder={t('screens.auth.redefinePassword.txtConfirmPass')}
              value={confirmPassword}
              onChange={(data: { value: string }) =>
                setConfirmPassword(data.value.trim())
              }
              validator={formErrors}
              loading={loading}
              onKeyPress={onInputKeyPress}
            />

            <AuthStyles.Space height={10} />

            <AuthComponents.SubmitLogin
              data-test-id="btn-reset"
              onSubmit={validateForm}
              loading={loading}
              title={t('screens.auth.redefinePassword.btnSave')}
              disabled={!currentPassword || !allowSave}
              onKeyPress={onInputKeyPress}
            />
            <AuthStyles.Space height={20} />

            <span
              className="forgot-password-link-back"
              role="presentation"
              onClick={onGoBack}>
              {t('screens.auth.redefinePassword.btnCancel')}
            </span>
          </AuthStyles.BoxLogin>
        </form>
        <AuthStyles.Version>{`v${appVersion}`}</AuthStyles.Version>
      </div>
    </div>
  );
}
